<template>
  <div class="pa-4 pt-0">
    <template v-if="loading">
      <div>......Loading</div>
    </template>
    <template v-else-if="!baseItem">
      <div class="text-body-2 text-center py-4">Redirecting to rewards...</div>
    </template>
    <template v-else>
      <div class="shipping-title">
        <PageTitle :title="baseItem.name" />
        <order-expiration-timer v-if="baseItem"></order-expiration-timer>
      </div>
      <div class="mt-6">
        <v-img
          alt="reward-media-product"
          class="rounded-t-xl"
          cover
          max-height="180"
          :src="getMedia"
        ></v-img>
      </div>
      <div v-if="shouldShowAttributes" class="pt-4">
        <label class="font-weight-bold" for="attribute">
          Options
        </label>
        <span v-if="hasAttributesOverCurrentPoints" class="d-block text-body-2">
          Not enough points for some options.
        </span>
        <v-chip-group
          v-model="attributeSelected"
          active-class="bg-primary"
          column
        >
          <v-chip
            v-for="attr in baseItem.attributes"
            :key="attr.id"
            class="text-capitalize"
            :disabled="
              points.current_points < calculatePointsNeeded(attr) ||
                !hasTicketsAvailable(attr.id)
            "
            :variant="attributeSelected === attr.id ? 'flat' : 'outlined'"
            :value="attr.id"
          >
            {{ attr.name }}
          </v-chip>
        </v-chip-group>
      </div>
      <div class="pt-4">
        <label class="font-weight-bold" for="delivery-type">
          Delivery Type
        </label>
        <span v-if="isItemShipped">
          This order will be shipped.
        </span>
        <span v-else-if="isItemPickedUp">
          This order will be picked up.
        </span>
        <v-chip-group
          v-else
          v-model="deliveryMethod"
          active-class="bg-primary"
          column
        >
          <v-chip :variant="deliveryMethod === 'shipped' ? 'flat' : 'outlined'" value="shipped">
            Shipped
          </v-chip>
          <v-chip :variant="deliveryMethod === 'picked_up' ? 'flat' : 'outlined'" value="picked_up">
            Pickup
          </v-chip>
        </v-chip-group>
      </div>
      <div class="align-center pt-4">
        <p
          v-if="deliveryMethod === 'shipped'"
          v-html="baseItem.shipping_details"
        ></p>
        <div v-if="deliveryMethod === 'picked_up'">
          <p
            v-if="baseItem.pickup_instructions"
            v-html="baseItem.pickup_instructions"
          ></p>
          <p>{{ getVenueAddress }}</p>
        </div>
        <v-btn
          class="font-weight-bold text-white text-decoration-none rounded-pill shipping-title__btn"
          color="primary"
          :disabled="
            (shouldShowAttributes && !attributeSelected) ||
              !deliveryMethod ||
              submitting
          "
          size="large"
          variant="flat"
          width="100%"
          @click="submitOrder()"
        >
          <span>
            {{
              deliveryMethod === 'shipped'
                ? 'Go To Shipping Info'
                : 'Continue To Checkout'
            }}
          </span>
        </v-btn>
      </div>
      <existing-order-modal
        v-model="modal.existingOrder"
        @close-dialog="modal.existingOrder = false"
        @remove-item="confirmRemoveFromOrder"
      />
    </template>
  </div>
</template>

<script>
/* eslint-disable camelcase */
import OrderExpirationTimer from '@/components/shared/OrderExpirationTimer/index.vue';
import PageTitle from '@/components/shared/PageTitle/index.vue';
import {mapActions, mapGetters, mapState} from 'vuex';
import {pascalCase} from '@/helpers';
import ExistingOrderModal from './components/ExistingOrderModal.vue';

export default {
  name: 'ClaimReward',
  components: {
    ExistingOrderModal,
    OrderExpirationTimer,
    PageTitle
  },
  data() {
    return {
      attributeSelected: undefined,
      deliveryMethod: '',
      loading: true,
      submitting: false,
      modal: {
        existingOrder: false
      }
    };
  },
  computed: {
    ...mapGetters('cartOrders', {
      activeOrder: 'active',
      findOrderItem: 'findOrderItem',
      existingOrderItem: 'existingOrderItem'
    }),
    ...mapState(['offer', 'partner', 'partnerUser', 'points']),
    ...mapState('cartOrders', ['leftTime']),

    // this.$store.state.products[123] | Get the product, deal, package etc.
    baseItem() {
      const { typeId, type } = this.$route.params;
      const { dealId } = this.$route.query;
      const typeIdNum = parseInt(typeId, 10);
      const cart = this.$store.state.cart;
      if (type === 'product' && cart?.cartProducts) {
        if (dealId) {
          const byDeal = cart.cartProducts.byDealId?.[parseInt(dealId, 10)];
          return Array.isArray(byDeal)
            ? byDeal.find(p => p.id === typeIdNum)
            : undefined;
        }
        const fromById = cart.cartProducts.byId?.[typeIdNum];
        if (fromById) return fromById;
        const byDealId = cart.cartProducts.byDealId || {};
        const allProducts = Object.values(byDealId).flat();
        return allProducts.find(p => p.id === typeIdNum);
      }
      const cartKey = `cart${pascalCase(type)}s`;
      const slice = cart?.[cartKey];
      return slice?.byId?.[typeIdNum];
    },

    hasAttributesOverCurrentPoints() {
      return this.baseItem?.attributes.some(
        attr => this.points.current_points < this.calculatePointsNeeded(attr)
      );
    },

    isItemShipped() {
      return this.baseItem?.shipped && !this.baseItem?.picked_up;
    },

    isItemPickedUp() {
      return this.baseItem?.picked_up && !this.baseItem?.shipped;
    },

    orderItem() {
      return this.findOrderItem(this.$route.params.type, this.baseItem?.id);
    },

    getMedia() {
      if (this.baseItem?.media && this.baseItem?.media.length > 0) {
        const media = this.baseItem?.media.find(m => m.name === 'Hero');
        return media
          ? media.url
          : 'https://res.cloudinary.com/bandwango/image/upload/v1643751153/noimg_ahhwzj.jpg';
      }
      return '';
    },

    getVenueAddress() {
      const address = window.localStorage.getItem('venueAddress');
      return address || '';
    },

    shouldShowAttributes() {
      return (
        this.baseItem?.attributes &&
        this.baseItem?.attributes.length > 0 &&
        this.baseItem.attributes[0]?.id
      );
    }
  },

  methods: {
    ...mapActions('cartOrders', [
      'createOrder',
      'orderExpired',
      'updateOrderItems',
      'addItemToExistingOrder',
      'deleteItemFromOrder',
      'resetCart'
    ]),

    calculatePointsNeeded(attr) {
      return this.offer.points_ratio * Number(attr.retail_price);
    },

    hasTicketsAvailable(attrId) {
      return (
        !this.baseItem.ticketsAvailable ||
        this.baseItem.ticketsAvailable[attrId] > 0 ||
        this.baseItem.ticketsAvailable[0] > 0 ||
        this.baseItem.ticketsAvailable[attrId] === null ||
        this.baseItem.ticketsAvailable[0] === null
      );
    },

    async confirmRemoveFromOrder() {
      // remove this product from the order.
      await this.deleteItemFromOrder({
        orderId: this.activeOrder.id,
        itemType: `order_${this.existingOrderItem.parent.type}s`,
        itemId: this.existingOrderItem.id
      });

      // clear the cache/session in /store/cart/orders
      this.resetCart();

      this.modal.existingOrder = false;
    },

    linkToShippingInfo() {
      this.$router.push({
        name: 'ShippingInfo',
        params: {
          typeId: this.$route.params.typeId,
          type: this.$route.params.type
        },
        query: {
          giftable: this.baseItem.giftable,
          t: this.$route.query.t
        }
      });
    },

    linkToOrderReview() {
      this.$router.push({
        name: 'ReviewOrder',
        query: {
          t: this.$route.query.t
        }
      });
    },

    async submitOrder() {
      this.submitting = true;
      const { typeId: itemId, type: itemType } = this.$route.params;
      const qty = this.attributeSelected ? { [this.attributeSelected]: 1 } : 1;

      // CREATE ORDER AND ADD ITEM
      // -----------------------------------------
      // customer         object          optional
      // deliveryMethod   string          optional
      // fields           json            optional
      // itemId           integer
      // itemType         string
      // offerId          integer
      // partnerUserId    integer         optional
      // payWithPoints    boolean         optional
      // qty              int_or_object

      // UPDATE ITEM IN CART
      // -----------------------------------------
      // attributeId      integer         optional
      // deliveryMethod   string          optional
      // fields           json            optional

      // ADD ITEM TO EXISTING CART
      // -----------------------------------------
      // customer         object          optional
      // deliveryMethod   string          optional
      // fields           json            optional
      // itemId           integer
      // itemType         string
      // payWithPoints    boolean         optional
      // qty              int_or_object

      // BASE BODY PARAMS
      // -----------------------------------------
      const bodyParams = {
        offerId: this.offer.id,
        partnerUserId: this.partnerUser.id,
        dealId: this.baseItem.deal_id,
        qty,
        itemId,
        itemType,
        deliveryMethod: this.deliveryMethod,
        payWithPoints: true
      };

      // NO ACTIVE ORDER
      if (!this.activeOrder) {
        try {
          await this.createOrder(bodyParams);
        } catch (error) {
          console.error('We could not create your order', error);
        }
      }

      // WE HAVE AN ORDER ITEM
      else if (this?.orderItem?.id) {
        const updateParams = {
          orderId: this.activeOrder.id,
          itemId: this.orderItem.id,
          itemType: `order_${itemType}s`,
          attributeId: this.attributeSelected
        };
        await this.updateOrderItems({
          ...bodyParams,
          ...updateParams
        });
      }

      // ADD NEW ORDER ITEM
      else {
        const addParams = {
          orderId: this.activeOrder.id,
          // itemId: this?.orderItem?.parent?.id || undefined,
          // itemType: `order_${itemType}s`,
          attributeId: this.attributeSelected
        };
        await this.addItemToExistingOrder({
          ...bodyParams,
          ...addParams
        });
      }

      this.submitting = false;
      return this.deliveryMethod === 'shipped'
        ? this.linkToShippingInfo()
        : this.linkToOrderReview();
    }
  },

  async created() {
    const baseItemExists = !!this.baseItem;

    if (!baseItemExists) {
      this.loading = false;
      await this.$router.push({
        path: '/rewards',
        query: {
          t: this.$route.query.t
        }
      });
      return;
    }

    // Set default value for delivery method
    const { shipped, picked_up: pickedUp } = this.baseItem;
    if (shipped || pickedUp)
      this.deliveryMethod = shipped ? 'shipped' : 'picked_up';

    this.loading = false;

    // Get values from the cart item if exists
    this.deliveryMethod =
      this.orderItem?.delivery_method || this.deliveryMethod;
    if (this.orderItem?.attribute) {
      this.baseItem.attributes.forEach(attr => {
        if (attr.id === this.orderItem.attribute.id)
          this.attributeSelected = attr.id;
      });
    }

    if (!this.existingOrderItem) this.modal.existingOrder = false;
    else if (
      !(
        !!this.existingOrderItem &&
        parseInt(this.$route.params.typeId, 10) === this.existingOrderItem?.parent?.id &&
        this.$route.params.type === this.existingOrderItem?.parent?.type
      )
    )
      this.modal.existingOrder = true;
  }
};
</script>

<style lang="scss" scoped>
.shipping-title__btn {
  font-size: 0.875rem !important;
  font-weight: 500;
  letter-spacing: 0.0892857143em;
  min-height: 40px !important;
  height: 40px !important;
  border-radius: 20px !important;
}
</style>